// Палитра ⌘K (T050, T116, T137; FR-037 – FR-037б; contracts/ui.md, «Палитра ⌘K и «Создать ▾»»):
// пустой ввод — «Действия» (команды по правам), «Недавние» (5 последних записей и разделов;
// пока их нет — строка-пояснение, группа видна всегда), «Перейти» (разделы по правам, рядом —
// клавиши перехода G H, G U, G J; ux.md, раздел 8). Раздел находится по первым буквам
// названия: «журн» → «Журнал действий». Два режима строки ввода (ux.md, раздел 8): «Команды» —
// команды, переходы и найденные записи; «Поиск» — только найденные записи. Режим «Поиск» и Tab —
// у любого вошедшего: что ищется, решают права ролей (features/search/sources.ts) — пользователи,
// доступ сопровождения, журнал действий. Backspace в пустом поле возвращает
// к командам, `/` открывает палитру сразу в поиске. Найдены записи — последняя строка «Все
// результаты по «…»» ведёт на страницу поиска /search?q=…. Ничего не нашлось — «Ничего не
// найдено по «…»» и подсказка. Сбой поиска — в группе строка с причиной, кодом и номером
// операции, а не «Ничего не найдено»; «Повторить поиск» повторяет запрос, палитра остаётся
// открытой с тем же вводом и режимом (T135; пункт keepOpen палитры @kp/ui). Пока повтор идёт,
// строка сбоя и пункт на месте, у пункта — спиннер, внизу — «Поиск…» (T168).
// 015 (FR-021): «Выйти» при неотправленных правках таблиц протокола — окно с их числом
// (useConfirmedLogout меню).
// 022 (T031; COLLAB-02): у внешнего протоколы и пункты не ищутся (readsDocuments, readsControl —
// documents.list и control.items не запрашиваются), в «Перейти» нет «Настроек», как и в меню.
// 030 (T059; ASG-DESK-15; конституция X): у сеанса по ссылке — только то, что есть в его меню:
// «Перейти» — «Мои задачи» и «Помощь» (окно вопроса контролёру), «Действия» — «Выйти»; «Недавних»
// и поиска записей нет — ему открыты только свои пункты.
// 032 (T026; FR-002): «Входящие» — в «Перейти» у всех, и у сеанса по ссылке (пункт его меню),
// с клавишами G I (keys.ts).
import { CommandPalette as PaletteDialog } from '@kp/ui';
import type { CommandPaletteGroup, CommandPaletteItem, CommandPaletteMode } from '@kp/ui';
import {
  ChevronRight,
  History,
  KeyRound,
  LifeBuoy,
  LogOut,
  RotateCcw,
  Search,
  ShieldPlus,
  UserPlus,
  UserRound,
} from '@kp/ui/icons';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  SEARCH_FROM,
  failureText,
  searchHint,
  searchPlaceholder,
  useSearchResults,
} from '../features/search/sources.ts';
import { useShell } from './context.tsx';
import type { PaletteMode, ShellApi } from './context.tsx';
import { useCreateItems } from './create.tsx';
import { goShortcutFor } from './keys.ts';
import {
  can,
  canGrantSupport,
  canPaletteSearch,
  sections as sectionRights,
  visibleNavItems,
} from './permissions.ts';
import { useRecent } from './recent.ts';
import { useMe } from './session.ts';
import { useConfirmedLogout } from './Sidebar.tsx';
import { sectionLinks } from './settings-sections.ts';
import type { SectionLink } from './settings-sections.ts';

/** Поиск записей — с короткой задержкой на каждое нажатие, до 5 строк в группе. */
const SEARCH_DELAY_MS = 200;
const SEARCH_LIMIT = 5;

/** «Недавние» при пустом вводе видны всегда; пока ничего не открывали — это пояснение. */
export const RECENT_EMPTY_TEXT = 'Здесь появятся последние открытые записи и разделы';

/** «Все результаты по «моро»» — последняя строка, когда найдены записи. */
function allResultsLabel(query: string): string {
  return `Все результаты по «${query.trim()}»`;
}

/** Ё и е, регистр — не различаются. */
function fold(text: string): string {
  return text.toLocaleLowerCase('ru-RU').replaceAll('ё', 'е').trim();
}

/** Название начинается с ввода или одно из его слов: «журн», «прав», «заме». */
export function matchesTitle(title: string, query: string): boolean {
  const needle = fold(query);
  if (!needle) return true;
  const text = fold(title);
  return text.startsWith(needle) || text.split(/[\s«»"(),.:—-]+/).some((w) => w.startsWith(needle));
}

export function CommandPalette() {
  const shell = useShell();
  const me = useMe();
  const { paletteOpen, setPaletteOpen } = shell;
  const canSearch = canPaletteSearch(me.permissions);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<PaletteMode>('commands');
  // Каждое открытие — с пустого ввода и с режима, которым открыли: ⌘K — команды, `/` — поиск.
  const [wasOpen, setWasOpen] = useState(paletteOpen);
  if (paletteOpen !== wasOpen) {
    setWasOpen(paletteOpen);
    if (paletteOpen) {
      setQuery('');
      setMode(canSearch ? shell.paletteMode : 'commands');
    }
  }
  const current: PaletteMode = canSearch ? mode : 'commands';
  const modes: CommandPaletteMode[] = [
    {
      id: 'commands',
      label: 'Команды',
      icon: ChevronRight,
      placeholder: 'Введите команду или найдите…',
    },
    ...(canSearch
      ? [
          {
            id: 'search',
            label: 'Поиск',
            icon: Search,
            placeholder: searchPlaceholder(me.permissions),
          },
        ]
      : []),
  ];
  const confirmedLogout = useConfirmedLogout();
  return (
    <>
      <PaletteDialog
        open={paletteOpen}
        onOpenChange={(open) => setPaletteOpen(open)}
        label="Найти или перейти"
        query={query}
        onQueryChange={setQuery}
        highlight={query}
        modes={modes}
        mode={current}
        onModeChange={(next) => setMode(next === 'search' ? 'search' : 'commands')}
        {...usePaletteContent(query, current, shell, confirmedLogout.logout)}
        empty={
          <>
            <strong className="kp-strong">Ничего не найдено по «{query.trim()}»</strong>
            <span>Проверьте раскладку или откройте раздел из меню</span>
          </>
        }
      />
      {confirmedLogout.dialog}
    </>
  );
}

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface PaletteContent {
  groups: CommandPaletteGroup[];
  loading: boolean;
  allResults?: CommandPaletteItem;
}

interface Command {
  id: string;
  label: string;
  icon: CommandPaletteItem['icon'];
  run: () => void;
}

function usePaletteContent(
  query: string,
  mode: PaletteMode,
  shell: ShellApi,
  logout: () => void,
): PaletteContent {
  const me = useMe();
  const navigate = useNavigate();
  const createItems = useCreateItems();
  const recent = useRecent(me.user.id);

  const trimmed = query.trim();
  const q = useDebounced(trimmed, SEARCH_DELAY_MS);
  const results = useSearchResults(q, { limit: SEARCH_LIMIT, enabled: shell.paletteOpen });

  // Палитра сама закрывается и возвращает фокус до действия (CommandPalette в @kp/ui).
  const go = (href: string) => () => void navigate({ href });

  const searchReady = trimmed.length >= SEARCH_FROM;
  // Группы найденного — по источникам, доступным ролям; сбой — строка с причиной и кодом.
  const recordGroups: CommandPaletteGroup[] = searchReady
    ? results.map((result) => ({
        id: result.id,
        heading: result.heading,
        items: result.hits.map((hit) => ({
          id: hit.id,
          label: hit.label,
          sub: hit.sub,
          icon: result.icon,
          onSelect: () => void navigate(hit.link),
        })),
        emptyText: result.failure ? failureText(result) : undefined,
      }))
    : [];
  const failed = searchReady && results.some((result) => result.failure);
  // Повтор идёт: строка сбоя и «Повторить поиск» на месте (T168), у пункта — спиннер, внизу —
  // «Поиск…», даже если в других группах уже есть найденное.
  const retrying = searchReady && results.some((result) => result.retrying);
  // «Повторить поиск» — тот же запрос на месте: палитра не закрывается, ввод и режим остаются.
  const retryGroups: CommandPaletteGroup[] = failed
    ? [
        {
          id: 'retry',
          heading: '',
          items: [
            {
              id: 'search:retry',
              label: 'Повторить поиск',
              icon: RotateCcw,
              keepOpen: true,
              loading: retrying,
              onSelect: () => {
                for (const result of results) if (result.failure) result.retry();
              },
            },
          ],
        },
      ]
    : [];
  const foundCount = recordGroups.reduce((sum, group) => sum + group.items.length, 0);
  const pending = searchReady && (q !== trimmed || results.some((result) => result.pending));
  // Сбой без повтора — загрузки нет: путь вперёд — «Повторить поиск».
  const searching = (foundNothing: boolean) => retrying || (pending && foundNothing && !failed);
  // «Все результаты» — когда найденное есть: страница поиска по всем источникам (ux.md, раздел 8).
  const allResults: CommandPaletteItem | undefined =
    foundCount > 0
      ? {
          id: 'all-results',
          label: allResultsLabel(trimmed),
          icon: Search,
          onSelect: () => void navigate({ to: '/search', search: { q: trimmed } }),
        }
      : undefined;

  if (mode === 'search') {
    return {
      loading: searching(foundCount === 0),
      allResults,
      groups: searchReady
        ? [...recordGroups, ...retryGroups]
        : [
            {
              id: 'search-hint',
              heading: 'Поиск',
              items: [],
              emptyText: searchHint(me.permissions),
            },
          ],
    };
  }

  const commands: Command[] = [];
  const creatable = new Set(createItems.map((item) => item.kind));
  if (creatable.has('user')) {
    commands.push({
      id: 'cmd:user',
      label: 'Создать пользователя',
      icon: UserPlus,
      run: () => shell.openCreate('user'),
    });
  }
  if (canGrantSupport(me.permissions)) {
    commands.push({
      id: 'cmd:support',
      label: 'Выдать доступ сопровождению',
      icon: ShieldPlus,
      run: () => shell.openCreate('supportGrant'),
    });
  }
  if (can(me.permissions, ['profile.change_password'])) {
    commands.push({
      id: 'cmd:password',
      label: 'Сменить пароль',
      icon: KeyRound,
      run: () => void navigate({ to: '/settings/password', hash: 'password' }),
    });
  }
  commands.push({ id: 'cmd:logout', label: 'Выйти', icon: LogOut, run: logout });

  // 030: «Мои задачи» и «Главная» — как в меню (по связи с человеком). Сеансу по ссылке — только
  // пункты его меню: разделов настроек у него нет.
  const sections: SectionLink[] = me.linkSession
    ? visibleNavItems(me.permissions, me)
    : sectionLinks(me.permissions, me).filter((item) => !(me.external && item.to === '/settings'));
  // G J — «Фоновые задачи» у сопровождения, иначе «Журнал действий» (keys.ts).
  const jobsOnJ = can(me.permissions, sectionRights.jobs.operations);
  const actionItems = commands
    .filter((command) => matchesTitle(command.label, trimmed))
    .map<CommandPaletteItem>((command) => ({
      id: command.id,
      label: command.label,
      icon: command.icon,
      onSelect: command.run,
    }));
  const sectionItems = sections
    .filter((item) => matchesTitle(item.label, trimmed))
    .map<CommandPaletteItem>((item) => ({
      id: `go:${item.to}`,
      label: item.label,
      icon: item.icon,
      ...(item.sub ? { sub: item.sub } : {}),
      shortcut: goShortcutFor(item.to, jobsOnJ),
      onSelect: go(item.to),
    }));
  // «Помощь» сеанса по ссылке — пункт его меню рядом с «Моими задачами»: окно вопроса.
  if (me.linkSession && matchesTitle('Помощь', trimmed)) {
    sectionItems.push({
      id: 'go:help',
      label: 'Помощь',
      icon: LifeBuoy,
      onSelect: () => shell.openHelp(),
    });
  }

  if (!trimmed && me.linkSession) {
    return {
      loading: false,
      groups: [
        { id: 'go', heading: 'Перейти', items: sectionItems },
        { id: 'actions', heading: 'Действия', items: actionItems },
      ],
    };
  }

  if (!trimmed) {
    return {
      loading: false,
      groups: [
        { id: 'actions', heading: 'Действия', items: actionItems },
        {
          id: 'recent',
          heading: 'Недавние',
          emptyText: RECENT_EMPTY_TEXT,
          items: recent.map((item) => ({
            id: `recent:${item.key}`,
            label: item.label,
            icon: item.kind === 'user' ? UserRound : History,
            onSelect: go(item.href),
          })),
        },
        { id: 'go', heading: 'Перейти', items: sectionItems },
      ],
    };
  }

  return {
    loading: searching(sectionItems.length + actionItems.length + foundCount === 0),
    allResults,
    groups: [
      { id: 'go', heading: 'Перейти', items: sectionItems },
      { id: 'actions', heading: 'Действия', items: actionItems },
      ...recordGroups,
      ...retryGroups,
    ],
  };
}
